import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDateIcon } from "@vetty/icons";
import { Calendar, Input } from "@/components";
import {
  joinUtcDateTimeLocal,
  localDateToUtcCivilDateString,
  splitUtcDateTimeLocal,
  utcCivilDateStringToLocalNoonDate,
} from "./group-form-drawer.helpers";
import styles from "./UtcDateTimeField.module.scss";

type UtcDateTimeFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  optional?: boolean;
  required?: boolean;
};

export const UtcDateTimeField = ({
  id,
  label,
  value,
  onChange,
  disabled = false,
  optional = false,
  required = false,
}: UtcDateTimeFieldProps) => {
  const { date, time } = splitUtcDateTimeLocal(value);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 280,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const selected = utcCivilDateStringToLocalNoonDate(date);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 8,
      left: rect.left,
      width: Math.max(rect.width, 280),
    });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const setDate = (nextDate: string) => {
    if (!nextDate) {
      onChange("");
      return;
    }
    onChange(joinUtcDateTimeLocal(nextDate, time || "00:00"));
  };

  const setTime = (nextTime: string) => {
    if (!date) {
      onChange("");
      return;
    }
    onChange(joinUtcDateTimeLocal(date, nextTime));
  };

  const dateDisplay = date || (optional ? "Optional" : "Select date");

  return (
    <div className={styles["comp"]}>
      <span className={styles["comp__label"]}>
        {label}
        {required ? <span aria-hidden> *</span> : null}
      </span>
      <div className={styles["comp__row"]}>
        <div className={styles["comp__field"]}>
          <span className={styles["comp__field-label"]}>Date</span>
          <button
            ref={triggerRef}
            id={`${id}-date`}
            type="button"
            className={styles["comp__date-trigger"]}
            aria-label={`Date, ${dateDisplay}`}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={listboxId}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              setOpen((v) => !v);
            }}
          >
            <CalendarDateIcon style={{ width: 18, height: 18 }} aria-hidden />
            <span>{dateDisplay}</span>
          </button>
        </div>
        <div className={styles["comp__field"]}>
          <label htmlFor={`${id}-time`} className={styles["comp__field-label"]}>
            Time
          </label>
          <Input
            id={`${id}-time`}
            value={time}
            onChange={setTime}
            placeholder="HH:mm"
            disabled={disabled || !date}
            inputMode="numeric"
          />
        </div>
      </div>
      {optional && value ? (
        <button
          type="button"
          className={styles["comp__clear"]}
          disabled={disabled}
          onClick={() => onChange("")}
        >
          Clear
        </button>
      ) : null}

      {open
        ? createPortal(
            <div
              ref={menuRef}
              id={listboxId}
              role="dialog"
              aria-label={`${label} calendar`}
              data-calendar-popover="true"
              className={styles["comp__popover"]}
              style={{
                top: menuPos.top,
                left: menuPos.left,
                minWidth: menuPos.width,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Calendar
                mode="single"
                selected={selected}
                defaultMonth={selected}
                onSelect={(next) => {
                  if (!next) {
                    if (optional) setDate("");
                    return;
                  }
                  setDate(localDateToUtcCivilDateString(next));
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};
